<template>
  <ConfirmDialog
    v-model:visible="localVisible"
    title="Approving"
    message="Please select the PO number"
    lbl-btn-submit="Yes"
    lbl-btn-cancel="No"
    icon-popup="icon-popup-submit"
    icon-btn-name="check-mark-circle"
    :width="420"
    :auto-close-on-submit="false"
    @submit="handleSubmit"
    @cancel="handleCancel"
  >
    <template #content>
      <div class="po-selection-form">
        <!-- Purchase Order ID -->
        <div class="form-field mb-4">
          <label class="field-label">
            Purchase Order ID <span class="optional">(optional)</span>
          </label>
          <el-select
            v-model="selectedPOLine"
            placeholder="Select Purchase Order ID"
            class="w-full"
            size="large"
            :loading="loading"
            value-key="poNumber"
            clearable
          >
            <el-option
              v-for="po in poLines"
              :key="po.poLineKey"
              :label="`${po.poNumber} - ${po.vendorName}`"
              :value="po"
            />
          </el-select>
        </div>

        <!-- Vendor Info (readonly) -->
        <div class="vendor-row">
          <div class="vendor-field">
            <label class="field-label">Vendor ID</label>
            <el-input
              :model-value="selectedPOLine?.vendorAccount || ''"
              disabled
              placeholder="Vendor ID"
              size="large"
            />
          </div>
          <div class="vendor-field">
            <label class="field-label">Vendor Name</label>
            <el-input
              :model-value="selectedPOLine?.vendorName || ''"
              disabled
              placeholder="Vendor Name"
              size="large"
            />
          </div>
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from 'vue';
import ConfirmDialog from '@/components/v2/ConfirmDialog.vue';
import timesheetService from '@/services/timesheet.service';

interface POLine {
  poLineKey: number;
  companyID: string;
  poNumber: string;
  poLineId: string;
  workerId: string;
  vendorAccount: string;
  vendorName: string;
  isOpen: boolean;
  active: boolean;
  description: string | null;
  poLotID: string | null;
}

const props = defineProps<{
  visible: boolean;
  workerId: string;
  companyId?: string;
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'confirm', poLine: POLine | undefined): void;
  (e: 'cancel'): void;
}>();

const localVisible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val)
});

const loading = ref(false);
const poLines = ref<POLine[]>([]);
const selectedPOLine = ref<POLine | undefined>(undefined);

const fetchPOLines = async () => {
  if (!props.workerId) return;

  loading.value = true;
  try {
    const response = await timesheetService.getPOLines(props.workerId, props.companyId);
    poLines.value = response?.value || response || [];
    selectedPOLine.value = undefined;
  } catch (error) {
    poLines.value = [];
  } finally {
    loading.value = false;
  }
};

// Fetch PO lines when dialog opens
watch(() => props.visible, async (isVisible) => {
  if (isVisible && props.workerId) {
    await fetchPOLines();
  }
}, { immediate: true });

const handleSubmit = () => {
  emit('confirm', selectedPOLine.value);
  localVisible.value = false;
};

const handleCancel = () => {
  emit('cancel');
  localVisible.value = false;
};
</script>

<style lang="scss" scoped>
.po-selection-form {
  padding: 8px 20px 20px;
  text-align: left;

  .form-field {
    margin-bottom: 16px;
  }

  .field-label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 6px;
  }

  .optional {
    color: #909399;
    font-weight: 400;
  }

  .vendor-row {
    display: flex;
    gap: 16px;
  }

  .vendor-field {
    flex: 1;
  }

  :deep(.el-input.is-disabled .el-input__wrapper) {
    background-color: #f5f7fa;
  }
}
</style>
